import {
  API_PREFIX,
  ApiRoute,
  attemptResultRoute,
  type AccountView,
  type ApiError,
  type ApiErrorCode,
  type AttemptResponse,
  type AttemptResult,
  type AuthResponse,
  type ContentResponse,
  type CreateAttemptRequest,
  type EquipRequest,
  type LoginRequest,
  type MarkSeenRequest,
  type ProfileResponse,
  type ProgressResponse,
  type RegisterRequest,
  type Settings,
  type ShopResponse,
} from '@bioforge/protocol';

/**
 * HTTP-клиент сервера (SPEC.md, раздел 17): аккаунт, контент, прогресс, попытки уровней, профиль.
 * На устройстве хранится только токен сессии — всё остальное приходит с сервера.
 */

const TOKEN_KEY = 'bioforge.account-token';

/** Сервер ответил ошибкой API. */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiErrorCode | null,
  ) {
    super(code ?? `HTTP ${status}`);
  }
}

/** Сервер не ответил: нет сети или сервер недоступен. */
export class ConnectionError extends Error {}

/** Состояние соединения для индикатора (SPEC.md, 17.4). */
export type ConnectionState = 'online' | 'offline' | 'server-down';

export interface TokenStore {
  get(): string | null;
  set(token: string | null): void;
}

/** Токен в localStorage; без хранилища (приватный режим) — только в памяти вкладки. */
export function browserTokenStore(): TokenStore {
  let memory: string | null = null;
  return {
    get() {
      try {
        return localStorage.getItem(TOKEN_KEY) ?? memory;
      } catch {
        return memory;
      }
    },
    set(token) {
      memory = token;
      try {
        if (token) localStorage.setItem(TOKEN_KEY, token);
        else localStorage.removeItem(TOKEN_KEY);
      } catch {
        // Хранилище недоступно — токен живёт до закрытия вкладки.
      }
    },
  };
}

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

export interface ApiClientOptions {
  readonly baseUrl: string;
  readonly tokens: TokenStore;
  readonly fetch?: Fetch;
  /** Сообщает индикатору, доступен ли сервер. */
  readonly onConnection?: (state: ConnectionState) => void;
  /** Пауза перед повтором отправки результата, мс (по умолчанию нарастающая). */
  readonly retryDelayMs?: (attempt: number) => number;
}

export class ApiClient {
  private readonly fetch: Fetch;
  private account: AccountView | null = null;

  constructor(private readonly options: ApiClientOptions) {
    this.fetch = options.fetch ?? ((input, init) => fetch(input, init));
  }

  /** Текущий аккаунт (после `ensureAccount`). */
  get current(): AccountView {
    if (!this.account) throw new Error('Аккаунт ещё не загружен');
    return this.account;
  }

  /**
   * Аккаунт для этого устройства: по сохранённому токену, иначе новый гость
   * (SPEC.md, 17.2). Отозванный или чужой токен заменяется гостем.
   */
  async ensureAccount(): Promise<AccountView> {
    if (this.options.tokens.get()) {
      try {
        this.account = await this.request<AccountView>('GET', ApiRoute.AccountMe);
        return this.account;
      } catch (error) {
        if (!(error instanceof ApiRequestError && error.status === 401)) throw error;
        this.options.tokens.set(null);
      }
    }
    return this.signIn(await this.request<AuthResponse>('POST', ApiRoute.AccountGuest));
  }

  /** Гость становится полноценным аккаунтом с тем же прогрессом. */
  async register(request: RegisterRequest): Promise<AccountView> {
    this.account = await this.request<AccountView>('POST', ApiRoute.AccountRegister, request);
    return this.account;
  }

  /** Вход заменяет аккаунт на устройстве; прогресс гостя остаётся в гостевом аккаунте. */
  async login(request: LoginRequest): Promise<AccountView> {
    return this.signIn(await this.request<AuthResponse>('POST', ApiRoute.AccountLogin, request));
  }

  /** Выход отзывает токен; дальше устройство играет новым гостем. */
  async logout(): Promise<AccountView> {
    try {
      await this.request<null>('POST', ApiRoute.AccountLogout);
    } catch (error) {
      if (!(error instanceof ApiRequestError)) throw error;
    }
    this.options.tokens.set(null);
    this.account = null;
    return this.ensureAccount();
  }

  content(): Promise<ContentResponse> {
    return this.request('GET', ApiRoute.Content);
  }

  progress(): Promise<ProgressResponse> {
    return this.request('GET', ApiRoute.Progress);
  }

  /** Отметки «уже видел» и настройки. */
  profile(): Promise<ProfileResponse> {
    return this.request('GET', ApiRoute.Profile);
  }

  async markSeen(request: MarkSeenRequest): Promise<void> {
    await this.request<null>('POST', ApiRoute.ProfileSeen, request);
  }

  saveSettings(settings: Settings): Promise<Settings> {
    return this.request('PUT', ApiRoute.ProfileSettings, settings);
  }

  /** Каталог магазина, предметы игрока и надетые скины. */
  shop(): Promise<ShopResponse> {
    return this.request('GET', ApiRoute.Shop);
  }

  equip(request: EquipRequest): Promise<ShopResponse> {
    return this.request('PUT', ApiRoute.ShopEquip, request);
  }

  createAttempt(request: CreateAttemptRequest): Promise<AttemptResponse> {
    return this.request('POST', ApiRoute.Attempts, request);
  }

  /**
   * Отправляет журнал партии. При обрыве связи повторяет, пока сервер не ответит
   * (SPEC.md, 5.3): повторная отправка той же попытки на сервере идемпотентна.
   * `cancelled` прекращает попытки (игрок ушёл с экрана).
   */
  async submitAttempt(
    attemptId: string,
    journal: string,
    cancelled: () => boolean = () => false,
  ): Promise<AttemptResult> {
    const delay = this.options.retryDelayMs ?? ((n) => Math.min(30_000, 1000 * 2 ** n));
    for (let attempt = 0; ; attempt++) {
      try {
        return await this.request<AttemptResult>('POST', attemptResultRoute(attemptId), {
          journal,
        });
      } catch (error) {
        if (!(error instanceof ConnectionError) || cancelled()) throw error;
        await new Promise((resolve) => setTimeout(resolve, delay(attempt)));
      }
    }
  }

  private signIn(auth: AuthResponse): AccountView {
    this.options.tokens.set(auth.token);
    this.account = auth.account;
    return auth.account;
  }

  private async request<T>(method: string, route: string, body?: unknown): Promise<T> {
    const token = this.options.tokens.get();
    let response: Response;
    try {
      response = await this.fetch(`${this.options.baseUrl}/${API_PREFIX}/${route}`, {
        method,
        headers: {
          ...(body === undefined ? {} : { 'content-type': 'application/json' }),
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
    } catch {
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
      this.options.onConnection?.(offline ? 'offline' : 'server-down');
      throw new ConnectionError('Сервер недоступен');
    }
    if (response.status >= 500) {
      this.options.onConnection?.('server-down');
      throw new ConnectionError(`Сервер ответил ${response.status}`);
    }
    this.options.onConnection?.('online');
    const text = await response.text();
    const data: unknown = text ? JSON.parse(text) : null;
    if (!response.ok) {
      const code = (data as Partial<ApiError> | null)?.error ?? null;
      throw new ApiRequestError(response.status, code);
    }
    return data as T;
  }
}
